// LinePlot.js
import { useState, useEffect, useCallback, useMemo } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors, formateTimeRange } from "../utils";

const Plot = createPlotlyComponent(Plotly);

// Keep in sync with filters: proxy profile is returned under this uuid
const PROXY_PROFILE_UUID = "5666258c-3f67-4f16-8167-bff4a1cac760";

const LinePlot = ({ isLoading, data, timebuckets, activeDomains, activeProxy }) => {
  const [chartData, setChartData] = useState(null);
  const [chartLayout, setChartLayout] = useState(null);
  const [showPlot, setShowPlot] = useState(false);

  const proxyId = activeProxy?.value ?? null;

  // Determine which key in `data` actually holds the profile:
  // - Ideally: keyed by proxy uuid (activeProxy.value)
  // - Fallback: some flows might still have the shared uuid key
  const dataKey = useMemo(() => {
    if (!data || !proxyId) return null;
    if (data[proxyId]) return proxyId;
    if (data[PROXY_PROFILE_UUID]) return PROXY_PROFILE_UUID;
    return null;
  }, [data, proxyId]);

  const createChartLayout = useCallback(() => {
    const plotLayout = {
      title: "",
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      xaxis: {
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
        type: "date",
        tickformat: "%Y",
      },
      yaxis: {
        range: [0, 100],
        linecolor: "white",
        gridcolor: "rgba(255,255,255, 0.1)",
      },
      font: { color: "white" },
      // helps centering when used inside flex containers
      margin: { l: 60, r: 20, t: 20, b: 50 },
    };
    setChartLayout(plotLayout);
  }, []);

  const createChartData = useCallback(() => {
    if (!dataKey) return;

    const profile = data?.[dataKey];
    if (!profile) return;

    // Only plot timestamp buckets from ~2015 and up (kept from your original)
    // Guard if buckets are smaller than 6
    const startIdx = Math.min(5, Math.max((timebuckets?.length ?? 0) - 1, 0));
    const slicedTimeBuckets = (timebuckets ?? []).slice(startIdx);
    const formattedTimeBuckets = slicedTimeBuckets.map((tb) => formateTimeRange(tb));

    // X axis wants actual date strings; you previously extracted "YYYY" from "YYYY / YYYY"
    const xYears = formattedTimeBuckets.map((ts) => ts.split("/")[0].trim());

    const plotData = (activeDomains ?? []).map((domain, idx) => {
      const dKey = domain?.value;
      if (!dKey) return null;

      const series = profile?.[dKey];
      if (!series) return null;

      const values = Array.isArray(series.value) ? series.value : [];
      const lbs = Array.isArray(series.value_lb) ? series.value_lb : [];
      const ubs = Array.isArray(series.value_ub) ? series.value_ub : [];

      // Slice the same way as timebuckets
      const vSlice = values.slice(startIdx);
      const lbSlice = lbs.slice(startIdx);
      const ubSlice = ubs.slice(startIdx);

      // Build intervals per timebucket
      const intervals = slicedTimeBuckets.map((_, i) => {
        const v = vSlice[i];
        const lb = lbSlice[i];
        const ub = ubSlice[i];
        return `${v ?? "NaN"} [${lb ?? "NaN"}, ${ub ?? "NaN"}]`;
      });

      const rUpper = intervals.map((interval) =>
        parseFloat(interval.split(" [")[1].split(", ")[1].slice(0, -1))
      );
      const rLower = intervals.map((interval) =>
        parseFloat(interval.split("[")[1].split(", ")[0])
      );
      const rValue = intervals.map((interval) => parseFloat(interval.split(" [")[0]));

      const baseColor = colors[idx % colors.length];
      const fillColor = baseColor.replace("rgb", "rgba").replace(")", ",0.4)");

      return [
        {
          x: xYears,
          y: rUpper,
          type: "scatter",
          mode: "lines",
          line: { color: "transparent" },
          showlegend: false,
          hoverinfo: "none",
        },
        {
          x: xYears,
          y: rLower,
          type: "scatter",
          mode: "lines",
          fill: "tonexty",
          fillcolor: fillColor,
          line: { color: "transparent" },
          showlegend: false,
          hoverinfo: "none",
        },
        {
          x: xYears,
          y: rValue,
          type: "scatter",
          mode: "lines+markers",
          line: { color: baseColor, width: 2 },
          marker: { size: 6 },
          name: domain.label,
          customdata: intervals,
          hovertemplate: `<b>${domain.label}</b>: %{customdata}<extra></extra>`,
        },
      ];
    });

    setChartData(plotData.filter(Boolean).flat());
  }, [activeDomains, data, dataKey, timebuckets]);

  useEffect(() => {
    const ready =
      !isLoading &&
      !!data &&
      !!dataKey &&
      !!activeProxy &&
      (activeDomains?.length ?? 0) > 0 &&
      (timebuckets?.length ?? 0) > 0;

    if (!ready) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeProxy,
    data,
    dataKey,
    timebuckets,
    createChartData,
    createChartLayout,
    isLoading,
  ]);

  return (
    <div className="flex flex-col items-center content-center w-full">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartData && chartLayout && showPlot && (
        <div className="w-full flex justify-center">
          <Plot data={chartData} layout={chartLayout} />
        </div>
      )}
    </div>
  );
};

export default LinePlot;